import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Award, ArrowLeft, LayoutDashboard, Sparkles, Check, Trophy,
  Zap, Plus, Trash2, Lightbulb, Wand2, Save, Bot, CheckCircle2, AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AGENT_LABELS: Record<string, { name: string; role: string; emoji: string }> = {
  veilleur: { name: "Veilleur", role: "Détection d'anomalies", emoji: "👁️" },
  qualificateur: { name: "Qualificateur", role: "Scoring prospects", emoji: "🎯" },
  redacteur: { name: "Rédacteur", role: "Séquences de prospection", emoji: "✍️" },
  analyste: { name: "Analyste", role: "Briefing stratégique", emoji: "📊" },
  prospecteur: { name: "Prospecteur", role: "Acquisition", emoji: "🚀" },
  nurturing: { name: "Nurturing", role: "Relance & engagement", emoji: "💖" },
  comptable: { name: "Comptable", role: "Suivi financier", emoji: "💰" },
  juridique: { name: "Juridique", role: "Conformité", emoji: "⚖️" },
  rh: { name: "RH", role: "Recrutement", emoji: "👥" },
};

interface LibraryItem {
  id: string;
  name: string;
  emoji: string;
  tagline: string;
  description: string;
}

interface CustomSkill {
  id: string;
  name: string;
  description: string;
  prompt_addon: string;
  enabled: boolean;
  created_at: string;
}

interface Tool {
  id: string;
  name: string;
  emoji: string;
  category: string;
  description: string;
  requires_credentials: boolean;
  credentials_hint?: string;
  enabled: boolean;
  credentials_ok: boolean;
  operational: boolean;
}

interface MyCompetences {
  active_mindsets: string[];
  active_strategies: string[];
  agent_overrides: Record<string, string>;
  custom_skills: CustomSkill[];
}

export default function CompetencesPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [library, setLibrary] = useState<{ mindsets: LibraryItem[]; strategies: LibraryItem[] }>({ mindsets: [], strategies: [] });
  const [me, setMe] = useState<MyCompetences>({ active_mindsets: [], active_strategies: [], agent_overrides: {}, custom_skills: [] });
  const [newSkill, setNewSkill] = useState({ name: "", description: "", prompt_addon: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<{ mindsets: any[]; strategies: any[] } | null>(null);
  const [agentDrafts, setAgentDrafts] = useState<Record<string, string>>({});
  const [tools, setTools] = useState<Tool[]>([]);
  const [toolsStats, setToolsStats] = useState({ available: 0, enabled: 0, operational: 0 });

  const loadTools = async () => {
    try {
      const r = await fetch(`${API_URL}/api/tools/catalog`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      setTools(d.tools || []);
      setToolsStats({
        available: d.total_available || 0,
        enabled: d.total_enabled || 0,
        operational: d.total_operational || 0,
      });
    } catch { /* ignore */ }
  };

  const toggleTool = async (toolId: string, enabled: boolean) => {
    try {
      const r = await fetch(`${API_URL}/api/tools/${toolId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (r.ok) {
        await loadTools();
        toast.success(enabled ? "Tool activé" : "Tool désactivé");
      }
    } catch {
      toast.error("Erreur");
    }
  };

  const autoSuggest = async () => {
    setSuggesting(true);
    setSuggestOpen(true);
    try {
      const res = await fetch(`${API_URL}/api/competences/auto-suggest`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        setSuggestions(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur");
        setSuggestOpen(false);
      }
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = async (kind: "mindset" | "strategy", id: string) => {
    await toggle(kind, id, true);
  };

  const applyAllSuggestions = async () => {
    if (!suggestions) return;
    for (const m of suggestions.mindsets) await toggle("mindset", m.id, true);
    for (const s of suggestions.strategies) await toggle("strategy", s.id, true);
    toast.success("Toutes les suggestions activées");
    setSuggestOpen(false);
  };

  const saveAgentPrompt = async (agentId: string) => {
    const value = agentDrafts[agentId] ?? me.agent_overrides[agentId] ?? "";
    const res = await fetch(`${API_URL}/api/competences/agent-prompt/${agentId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ prompt_override: value || null }),
    });
    if (res.ok) {
      setMe((prev) => ({
        ...prev,
        agent_overrides: value ? { ...prev.agent_overrides, [agentId]: value } : Object.fromEntries(Object.entries(prev.agent_overrides).filter(([k]) => k !== agentId)),
      }));
      toast.success(`Agent ${AGENT_LABELS[agentId]?.name} mis à jour`);
    }
  };

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [libRes, meRes] = await Promise.all([
          fetch(`${API_URL}/api/competences/library`, { credentials: "include" }),
          fetch(`${API_URL}/api/competences/me`, { credentials: "include" }),
        ]);
        if (libRes.ok) setLibrary(await libRes.json());
        if (meRes.ok) setMe(await meRes.json());
      } catch {/* empty */}
      loadTools();
    })();
  }, [user]);

  const toggle = async (kind: "mindset" | "strategy", id: string, next: boolean) => {
    const res = await fetch(`${API_URL}/api/competences/${kind}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ enabled: next }),
    });
    if (res.ok) {
      setMe((prev) => {
        const key = kind === "mindset" ? "active_mindsets" : "active_strategies";
        const list = new Set(prev[key]);
        if (next) list.add(id); else list.delete(id);
        return { ...prev, [key]: [...list] };
      });
      toast.success(`${next ? "Activé" : "Désactivé"}`);
    }
  };

  const addCustom = async () => {
    if (!newSkill.name.trim() || !newSkill.prompt_addon.trim()) {
      toast.error("Nom et instruction requis");
      return;
    }
    const res = await fetch(`${API_URL}/api/competences/custom`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(newSkill),
    });
    if (res.ok) {
      const created = await res.json();
      setMe((prev) => ({ ...prev, custom_skills: [created, ...prev.custom_skills] }));
      setNewSkill({ name: "", description: "", prompt_addon: "" });
      setAddOpen(false);
      toast.success("Compétence ajoutée à Grafton");
    }
  };

  const deleteCustom = async (skillId: string) => {
    if (!confirm("Supprimer cette compétence ?")) return;
    const res = await fetch(`${API_URL}/api/competences/custom/${skillId}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      setMe((prev) => ({ ...prev, custom_skills: prev.custom_skills.filter((s) => s.id !== skillId) }));
      toast.success("Compétence supprimée");
    }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Sparkles className="h-8 w-8 animate-pulse text-primary" /></div>;
  if (!user) return null;

  const renderCard = (item: LibraryItem, kind: "mindset" | "strategy") => {
    const active = (kind === "mindset" ? me.active_mindsets : me.active_strategies).includes(item.id);
    return (
      <Card
        key={item.id}
        className={`relative overflow-hidden transition-all cursor-pointer ${active ? "border-primary/60 border-2 bg-primary/5" : "border-border/50 hover:border-primary/30"}`}
        onClick={() => toggle(kind, item.id, !active)}
        data-testid={`${kind}-${item.id}`}
      >
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between">
            <div className="text-3xl leading-none">{item.emoji}</div>
            <div className={`h-6 w-6 rounded-full flex items-center justify-center border-2 transition-all ${active ? "bg-primary border-primary" : "border-border/60"}`}>
              {active && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-bold leading-tight">{item.name}</h4>
            <p className="text-xs text-primary/80 mt-0.5 font-medium">{item.tagline}</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="competences-page">
      <div className="border-b border-border/50 bg-card/30 backdrop-blur px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">Compétences de Grafton</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /> Retour</Button>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/dashboard")}><LayoutDashboard className="h-4 w-4" /> Dashboard</Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card p-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30">
              <Trophy className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">Tools & Intelligence de Grafton</span>
            </div>
            <h2 className="text-3xl font-bold leading-tight">
              Ce que Grafton <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">fait vraiment</span> vs <span className="text-muted-foreground">comment il raisonne</span>
            </h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              <b className="text-emerald-400">Tools</b> = actions techniques réellement exécutables (email, CRM, trésorerie, paiement…).<br/>
              <b className="text-blue-400">Mindsets & Stratégies</b> = influence le <i>raisonnement</i> et le style des réponses.<br/>
              <b className="text-amber-400">Custom skills</b> = consignes textuelles (zéro capacité technique ajoutée).
            </p>
            <div className="flex flex-wrap gap-4 pt-2 text-xs" data-testid="competences-stats">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="text-muted-foreground">{toolsStats.operational} tool(s) opérationnel(s)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-blue-400" />
                <span className="text-muted-foreground">{me.active_mindsets.length} mindset(s) · {me.active_strategies.length} stratégie(s)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-400" />
                <span className="text-muted-foreground">{me.custom_skills.filter((s) => s.enabled).length} compétence(s) custom</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="tools" className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <TabsList className="bg-muted/30">
              <TabsTrigger value="tools" data-testid="tab-tools">🔧 Tools réels</TabsTrigger>
              <TabsTrigger value="mindsets" data-testid="tab-mindsets">🧠 Mindsets</TabsTrigger>
              <TabsTrigger value="strategies" data-testid="tab-strategies">🎯 Stratégies</TabsTrigger>
              <TabsTrigger value="custom" data-testid="tab-custom">⚡ Custom</TabsTrigger>
              <TabsTrigger value="agents" data-testid="tab-agents">🤖 Agents NanoCorp</TabsTrigger>
            </TabsList>
            <Button size="sm" variant="outline" className="gap-1.5 border-primary/40 text-primary hover:bg-primary/10" onClick={autoSuggest} data-testid="auto-suggest-btn">
              <Wand2 className="h-3.5 w-3.5" /> Suggestions IA
            </Button>
          </div>

          <TabsContent value="tools" className="space-y-4">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3" data-testid="tools-transparency-banner">
              <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-emerald-300">Actions techniques réellement exécutables</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Ce que Grafton peut concrètement <b>déclencher</b> côté backend : envois d'emails, lectures CRM, paiements, génération de dossiers, etc. Activez ceux que vous voulez rendre disponibles. Les tools nécessitant des credentials (🔑) doivent d'abord être configurés dans <a href="/connexions-api" className="underline text-emerald-400">Connexions API</a>.
                </p>
              </div>
            </div>

            {tools.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Chargement des tools…</p>
            ) : (
              <>
                <div className="flex items-center gap-3 flex-wrap" data-testid="tools-stats-row">
                  <Badge variant="default" className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                    {toolsStats.operational} opérationnel(s)
                  </Badge>
                  <Badge variant="secondary">{toolsStats.enabled} activé(s) / {toolsStats.available} disponibles</Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tools-grid">
                  {tools.map((t) => (
                    <Card
                      key={t.id}
                      data-testid={`tool-card-${t.id}`}
                      className={`border transition-all ${
                        t.operational
                          ? "border-emerald-500/40 bg-emerald-500/5"
                          : t.enabled && t.requires_credentials && !t.credentials_ok
                          ? "border-amber-500/40 bg-amber-500/5"
                          : "border-border/50"
                      }`}
                    >
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            <div className="text-2xl shrink-0">{t.emoji}</div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <h4 className="text-sm font-semibold leading-tight">{t.name}</h4>
                                {t.operational && (
                                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                                    Opérationnel
                                  </Badge>
                                )}
                                {t.enabled && t.requires_credentials && !t.credentials_ok && (
                                  <Badge variant="outline" className="border-amber-500/40 text-amber-400 text-[10px]">
                                    🔑 Credentials requis
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground/70 mt-0.5">{t.category}</p>
                            </div>
                          </div>
                          <Switch
                            checked={t.enabled}
                            onCheckedChange={(v) => toggleTool(t.id, v)}
                            data-testid={`tool-toggle-${t.id}`}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{t.description}</p>
                        {t.enabled && t.requires_credentials && !t.credentials_ok && t.credentials_hint && (
                          <p className="text-xs text-amber-400/90">💡 {t.credentials_hint}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="mindsets" className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider">Mindsets disponibles</h3>
              <p className="text-xs text-muted-foreground">Cliquez pour activer/désactiver. Plusieurs mindsets peuvent coexister.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {library.mindsets.map((m) => renderCard(m, "mindset"))}
            </div>
          </TabsContent>

          <TabsContent value="strategies" className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider">Stratégies business</h3>
              <p className="text-xs text-muted-foreground">Choisissez la stratégie qui guidera les décisions de Grafton.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {library.strategies.map((s) => renderCard(s, "strategy"))}
            </div>
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="custom-warning-banner">
              <div className="h-8 w-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-amber-300">Consignes de comportement — pas de capacité technique</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Les compétences custom sont <b>injectées comme texte</b> dans chaque réponse de Grafton. Elles influencent son <b>style</b> et son <b>raisonnement</b>, mais n'ajoutent <b>aucune capacité technique</b>. Si vous voulez que Grafton déclenche une vraie action (email, CRM, paiement…), activez le <a href="#" onClick={(e) => { e.preventDefault(); (document.querySelector('[data-testid=tab-tools]') as HTMLElement)?.click(); }} className="underline text-emerald-400">tool correspondant</a>.
                </p>
              </div>
            </div>
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold uppercase tracking-wider">Vos compétences custom</h3>
                <p className="text-xs text-muted-foreground max-w-xl">Enseignez à Grafton une compétence spécifique à votre business. Chaque compétence est injectée comme un "super pouvoir" permanent.</p>
              </div>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="gap-1.5" data-testid="add-custom-skill"><Plus className="h-3.5 w-3.5" /> Ajouter</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Nouvelle compétence</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs">Nom de la compétence</Label>
                      <Input placeholder="Ex: Expert closing B2B" value={newSkill.name} onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })} data-testid="skill-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Description (optionnel)</Label>
                      <Input placeholder="Ex: Spécialiste des ventes complexes avec cycle long" value={newSkill.description} onChange={(e) => setNewSkill({ ...newSkill, description: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs flex items-center gap-1"><Lightbulb className="h-3 w-3 text-primary" /> Instruction pour Grafton</Label>
                      <Textarea
                        placeholder="Ex: Quand tu rédiges des emails de prospection, utilise la méthode Challenger Sale. Mentionne 3 insights sur le marché de l'audiovisuel avant de parler de notre offre. Ne termine jamais sans un CTA clair avec 2 créneaux proposés."
                        value={newSkill.prompt_addon}
                        onChange={(e) => setNewSkill({ ...newSkill, prompt_addon: e.target.value })}
                        className="min-h-[110px] text-xs"
                        data-testid="skill-prompt-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>Annuler</Button>
                      <Button size="sm" onClick={addCustom} data-testid="skill-save-btn">Enregistrer</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {me.custom_skills.length === 0 ? (
              <Card className="border-border/50 border-dashed"><CardContent className="p-10 text-center">
                <Zap className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground mb-2">Aucune compétence custom.</p>
                <p className="text-xs text-muted-foreground/70 max-w-sm mx-auto">Exemple : "Copywriter 40K€ de conversion", "Expert SEO local", "Négociateur ventes complexes"...</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {me.custom_skills.map((skill) => (
                  <Card key={skill.id} className="border-border/50" data-testid={`custom-skill-${skill.id}`}>
                    <CardContent className="p-5 flex items-start gap-4">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <h4 className="text-sm font-bold">{skill.name}</h4>
                        {skill.description && <p className="text-xs text-primary/80">{skill.description}</p>}
                        <p className="text-xs text-muted-foreground leading-relaxed mt-2">{skill.prompt_addon}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-red-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => deleteCustom(skill.id)} data-testid={`delete-skill-${skill.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="agents" className="space-y-4">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold uppercase tracking-wider">9 agents NanoCorp</h3>
              <p className="text-xs text-muted-foreground">Ajoutez des instructions custom à chaque agent. Elles s'ajoutent au prompt de base lors des scans quotidiens.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(AGENT_LABELS).map(([agentId, meta]) => {
                const current = agentDrafts[agentId] ?? me.agent_overrides[agentId] ?? "";
                const hasOverride = !!me.agent_overrides[agentId];
                const isDirty = (agentDrafts[agentId] ?? "") !== (me.agent_overrides[agentId] ?? "") && agentDrafts[agentId] !== undefined;
                return (
                  <Card key={agentId} className={`border-border/50 ${hasOverride ? "border-primary/40" : ""}`} data-testid={`agent-${agentId}`}>
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-muted/30 border border-border/50 flex items-center justify-center text-lg">
                            {meta.emoji}
                          </div>
                          <div>
                            <h4 className="text-sm font-bold">Agent {meta.name}</h4>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{meta.role}</p>
                          </div>
                        </div>
                        {hasOverride && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary font-semibold">Personnalisé</span>}
                      </div>
                      <Textarea
                        placeholder={`Instructions pour l'agent ${meta.name}. Exemple : "Mentionne toujours nos 3 services principaux et utilise un ton formel."`}
                        value={current}
                        onChange={(e) => setAgentDrafts((prev) => ({ ...prev, [agentId]: e.target.value }))}
                        className="min-h-[90px] text-xs"
                        data-testid={`agent-prompt-${agentId}`}
                      />
                      <div className="flex justify-end">
                        <Button size="sm" variant={isDirty ? "default" : "outline"} className="h-7 text-xs gap-1.5" onClick={() => saveAgentPrompt(agentId)} disabled={!isDirty} data-testid={`save-agent-${agentId}`}>
                          <Save className="h-3 w-3" /> Enregistrer
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Auto-suggest dialog */}
        <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-primary" />
                Suggestions IA basées sur votre ADN
              </DialogTitle>
            </DialogHeader>
            {suggesting ? (
              <div className="p-10 text-center">
                <Sparkles className="h-8 w-8 mx-auto text-primary animate-pulse mb-3" />
                <p className="text-sm text-muted-foreground">Grafton analyse votre profil...</p>
              </div>
            ) : suggestions ? (
              <div className="space-y-5">
                {suggestions.mindsets.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mindsets recommandés</p>
                    {suggestions.mindsets.map((m) => (
                      <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <div className="text-2xl leading-none">{m.emoji}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold">{m.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{m.reason}</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => applySuggestion("mindset", m.id)} disabled={me.active_mindsets.includes(m.id)}>
                          {me.active_mindsets.includes(m.id) ? <><Check className="h-3 w-3 mr-1" /> Actif</> : "Activer"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {suggestions.strategies.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Stratégie recommandée</p>
                    {suggestions.strategies.map((s) => (
                      <div key={s.id} className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                        <div className="text-2xl leading-none">{s.emoji}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold">{s.name}</p>
                          <p className="text-xs text-muted-foreground mt-1">{s.reason}</p>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => applySuggestion("strategy", s.id)} disabled={me.active_strategies.includes(s.id)}>
                          {me.active_strategies.includes(s.id) ? <><Check className="h-3 w-3 mr-1" /> Actif</> : "Activer"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 justify-end pt-3 border-t border-border/30">
                  <Button variant="outline" size="sm" onClick={() => setSuggestOpen(false)}>Fermer</Button>
                  <Button size="sm" onClick={applyAllSuggestions} className="gap-1.5"><Check className="h-3.5 w-3.5" /> Tout activer</Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
